import { Router } from "preact-router";
import { useState, useMemo } from "preact/hooks";
import { getQueryParamsFromUrl, isEmbeddedInWebview } from "@/lib/web";
import { postEvent } from "@/lib/api_controller";
import { TaaliaEvent } from "@/lib/event_types";
import { localStorageSet, STORAGE_KEYS } from "@/lib/storage";
import { nanoid } from "@/lib/nanoid";
import { doLog } from "@/lib/globals";
import ChatInterface from "@/ui/pages/ChatInterface";
import StripeCheckoutHandler from "@/ui/components/StripeCheckoutHandler";
import ActivityReportView from "@/ui/pages/activity_report/activityReportView";
import Logout from "@/ui/components/Logout";
import ActivityFeedback from "@/ui/pages/ci/ActivityFeedback";
import ActivityFeedbackPartial from "@/ui/pages/ci/ActivityFeedbackPartial";
import { useLocalStorage } from "@/lib/storage";
import OpenInBrowser from "@/ui/components/OpenInBrowser";
import LogRocket from "logrocket";

// MARK: Test Widgets
// import EmailPhoneInputHarness from "@/ui/_test_widgets/EmailPhoneInputHarness";
// import ChatScaffoldHarness from "@/ui/_test_widgets/ChatScaffoldHarness";
// import TestDdl from "@/ui/_test_widgets/TestDdl";
// import TextArea from "@/ui/_test_widgets/TestTextArea";
// import SkuOptionsHarness from "@/ui/_test_widgets/SkuOptionsHarness";

const App = () => {
  if (doLog()) console.log("App");

  LogRocket.init("yrpb82/taalia");

  // eslint-disable-next-line no-unused-vars
  const [sampleReport, setSampleReport] = useState(null);
  const [appLoadEventSent, setAppLoadEventSent] = useLocalStorage(
    STORAGE_KEYS.appLoadEventSent,
    false
  );

  // MARK: Embedded in Webview Check
  const embeddedInWebview = useMemo(() => {
    return isEmbeddedInWebview();
  }, []);

  if (!appLoadEventSent) {
    const newLcid = nanoid();
    if (doLog()) console.log("newLcid", newLcid);

    // Set lcid in local storage
    localStorageSet(STORAGE_KEYS.lcidKey, newLcid);

    // https://docs.logrocket.com/reference/identify
    LogRocket.identify(newLcid);

    // Send embedded or app load event
    if (embeddedInWebview) {
      // Send embedded in webview event
      postEvent(TaaliaEvent.embeddedInWebview, {
        embed_host: embeddedInWebview,
        userAgent: navigator.userAgent,
      });
    } else {
      // Send app load event
      postEvent(TaaliaEvent.appLoad, { query: getQueryParamsFromUrl() });
    }

    // Set app load event sent in session storage
    setAppLoadEventSent(true);
  }

  // MARK: Hotjar
  // useEffect(() => {
  //   window.hj =
  //     window.hj ||
  //     function () {
  //       (window.hj.q = window.hj.q || []).push(arguments);
  //     };
  //   if (doLog()) console.log("calling hj identify", lcid);
  //   window.hj("identify", lcid, { lcid });
  // }, [lcid]);

  // MARK: Clarity
  // https://learn.microsoft.com/en-us/clarity/filters/custom-tags
  // useEffect(() => {
  //   if (window.clarity) {
  //     window.clarity("set", "lcid", lcid);
  //   }
  // }, [lcid]);

  // MARK: Report Testing
  // useEffect(() => {
  //   import("../_SAMPLE_OBJECTS/sample_report_2").then((module) => {
  //     setSampleReport(module.sampleReport);
  //   });
  // }, []);

  // MARK: Embedded Modal
  // This needs to go here to ensure it is ALWAYS enforced -- reloads etc.
  if (embeddedInWebview) {
    return <OpenInBrowser />;
  }

  // MARK: LogRocket track route changes
  const handleRoute = (e) => {
    LogRocket.track(`Route: ${e.url}`);
  };

  // MARK: Main Render
  return (
    <Router onChange={handleRoute}>
      <ChatInterface path="/" />
      <ActivityFeedback path="/activity-feedback" />
      <ActivityFeedback path="/ci/af" />
      <ActivityFeedbackPartial path="/ci/afp" />
      <StripeCheckoutHandler path="/stripe-checkout-return" />
      <Logout path="/logout" />
      {sampleReport && (
        <ActivityReportView
          path="/debug/activity-report"
          assessmentParam={sampleReport}
        />
      )}
      {/* MARK: Test Widgets */}
      {/* <TextArea path="/debug/textarea" />
      <SkuOptionsHarness path="/debug/skus" />
      <EmailPhoneInputHarness path="/debug/email-phone-input" />
      <TestDdl path="/debug/test-ddl" />
      <ChatScaffoldHarness path="/debug/chat-layout" /> */}
      <NotFound default />
    </Router>
  );
};

export default App;

const NotFound = () => (
  <div style={{ textAlign: "center", padding: "50px" }}>
    <h1>404 - Page Not Found</h1>
    <p>The page you&apos;re looking for doesn&apos;t exist.</p>
  </div>
);
